import React, { useEffect, useState } from 'react';
import { Tag, Space, Dropdown, MenuProps, Modal } from 'antd';
import SideNav from '../company-settings/side-nav/component';
import { AddCustomField } from './add-modal';
import { axiosInstanceB2B, Toaster } from '@share/utils';
import { useSelector } from 'react-redux';
import { ILoginState } from '@share/store/slices';
import { Urls } from '@share/constants';
import { ExclamationCircleOutlined, MenuOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { arrayMoveImmutable } from 'array-move';
import type { SortableContainerProps, SortEnd } from 'react-sortable-hoc';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { CustomFieldDto, CustomFieldEnum } from '@common-types';
import { AdminTravBusiness, HasPermission } from '@utils';

import './style.scss';
import { BlueButton } from '@share/components';
import { FormattedMessage, useIntl } from 'react-intl';

const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />);

const SortableItem = SortableElement((props: React.HTMLAttributes<HTMLTableRowElement>) => (
  <tr {...props} />
));
const SortableBody = SortableContainer((props: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <tbody {...props} />
));

interface IMapStateToProps {
  loginStore: ILoginState;
}

const CompanyCustomFields: React.FC = () => {
  const login = useSelector((state: IMapStateToProps) => state.loginStore);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setEditIsOpen] = useState(false);
  const [customFields, setCustomFields] = useState<CustomFieldDto[]>([]);
  const [dptRoles, setDptRoles] = useState();
  const [selectedField, setSelectedField] = useState<CustomFieldDto>(new CustomFieldDto());
  const [newSelectedField, setNewSelectedField] = useState<CustomFieldEnum>();
  const [loading, setLoading] = useState(true);
  const { confirm } = Modal;

  const intl = useIntl();
  useEffect(() => {
    //if (HasPermission(login.user, AdminTravBusiness)) {
    fetchData();
    //}
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSelectedField((prev) => {
        const newField = new CustomFieldDto();
        return {
          ...newField,
          type: prev.type, 
        };
      });
    }
  }, [isOpen]);

  const onSortEnd = ({ oldIndex, newIndex }: SortEnd) => {
    if (oldIndex !== newIndex) {
      const fields = arrayMoveImmutable(customFields.slice(), oldIndex, newIndex).filter(
        (el: CustomFieldDto) => !!el,
      );
      fields.reduce(function (a, e, i) {
        let field = customFields.find((x: CustomFieldDto) => x.id == e.id);
        field.index = i + 1;
        return a;
      }, []);
      setCustomFields(fields);
      const response = axiosInstanceB2B.put(Urls.CustomFields, { fields });
    }
  };

  const DraggableContainer = (props: SortableContainerProps) => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow: React.FC<any> = ({ className, style, ...restProps }) => {
    const index = customFields.findIndex((x) => x.index === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  };

  const columns: ColumnsType<CustomFieldDto> = [
    {
      title: intl.formatMessage({ id: 'sort' }),
      dataIndex: 'index',
      width: '5%',
      className: 'drag-visible text-center',
      render: () => <DragHandle />,
    },
    {
      title: intl.formatMessage({ id: 'field.name' }),
      dataIndex: 'label',
      className: 'drag-visible',
      width: '40%',
    },
    {
      title: intl.formatMessage({ id: 'type' }),
      dataIndex: 'type',
      width: '20%',
      render: (type: CustomFieldEnum) => <span> {<Tag>{type}</Tag>} </span>,
    },
    {
      title: intl.formatMessage({ id: 'required' }),
      dataIndex: 'mandatory',
      width: '10%',
      className: 'text-center',
      render: (required: boolean) => (
        <span> {<Tag color={required ? 'green' : 'red'}>{required ? intl.formatMessage({ id: 'YES' }) : intl.formatMessage({ id: 'NO' })}</Tag>} </span>
      ),
    },
    {
      title: intl.formatMessage({ id: 'visibility' }),
      dataIndex: 'visibility',
      width: '15%',
      className: 'text-center',
      render: (visibility: string[]) => (
        <span>
          {' '}
          {visibility.map((item, idx) => (
            <Tag key={idx} className="mb-1">
              {item}
            </Tag>
          ))}{' '}
        </span>
      ),
    },
    {
      title: intl.formatMessage({ id: 'action' }),
      key: 'action',
      width: '10%',
      className: 'text-center',
      render: (_, record) => (
        <Space size="middle">
          <a onClick={() => handleEdit(record)}>
            <i className="bi bi-pencil-square"></i>
          </a>
          <a onClick={() => showPromiseConfirm(record.id)}>
            <i className="bi bi-trash"></i>
          </a>
        </Space>
      ),
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const parentId =
        login.account?.name != 'travbusiness' ? login.account?.parentAccountId : login.account?.id;
      const { data: fields } = await axiosInstanceB2B.get(`${Urls.CustomFields}`, {});
      const { data: getRoles } = await axiosInstanceB2B.get(
        `${Urls.GetUserRoles}?page=1&results=1000&accountId=${parentId}`,
        {},
      );

      setCustomFields(fields.result);
      setDptRoles(getRoles);
      setLoading(false);
    } catch (error: any) {
      Toaster.error(error.response?.data?.errors[0]?.message);
      setLoading(false);
    }
  };

  const handleSaveSuccess = async (values: CustomFieldDto) => {
    setLoading(true);
    try {
      const response = await axiosInstanceB2B.post(`${Urls.CustomFields}`, { field: values });
      Toaster.success(response.data.message);
      await fetchData();
    } catch (error: any) {
      Toaster.error(error.response?.data?.errors[0]?.message);
    } finally {
      setLoading(false);
    }
  };

  const showPromiseConfirm = (id: number) => {
    confirm({
      title: intl.formatMessage({ id: 'delete.items' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({ id: 'delete.items.message' }),
      onOk() {
        return new Promise((resolve, reject) => {
          handleDelete(id);
          setTimeout(Math.random() > 0.5 ? resolve : reject, 1000);
        }).catch((error) => console.error(error));
      },
      onCancel() {},
    });
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      const response = await axiosInstanceB2B.delete(`${Urls.CustomFields}/${id}`);
      Toaster.success(response.data.message);
      await fetchData();
    } catch (error: any) {
      Toaster.error(error.response?.data?.errors[0]?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (field: CustomFieldDto) => {
    const strToEnum = CustomFieldEnum[field.type] as unknown as CustomFieldEnum;
    setSelectedField({ ...field, type: strToEnum });
    setEditIsOpen(true);
  };

  const items: MenuProps['items'] = [
    {
      label: CustomFieldEnum[CustomFieldEnum.SingleText],
      key: CustomFieldEnum.SingleText,
      icon: <i className="bi bi-cursor-text"></i>,
      className: 'dropdown-padding-item',
    },
    {
      label: CustomFieldEnum[CustomFieldEnum.LongText],
      key: CustomFieldEnum.LongText,
      icon: <i className="bi bi-text-left"></i>,
      className: 'dropdown-padding-item',
    },
    {
      label: CustomFieldEnum[CustomFieldEnum.Checkbox],
      key: CustomFieldEnum.Checkbox,
      icon: <i className="bi bi-check2-square"></i>,
      className: 'dropdown-padding-item',
    },
    {
      label: CustomFieldEnum[CustomFieldEnum.Dropdown],
      key: CustomFieldEnum.Dropdown,
      icon: <i className="bi bi-menu-button-wide"></i>,
      className: 'dropdown-padding-item',
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    const strToEnum = e.key as unknown as CustomFieldEnum;
    setNewSelectedField(strToEnum);
    setSelectedField({ ...selectedField, type: strToEnum });
    setIsOpen(true);
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <>
      {isOpen && (
        <AddCustomField
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          dptRoles={dptRoles}
          fieldData={selectedField}
          onSaveSuccess={handleSaveSuccess}
        />
      )}
      {isEditOpen && (
        <AddCustomField
          isOpen={isEditOpen}
          setIsOpen={setEditIsOpen}
          dptRoles={dptRoles}
          fieldData={selectedField}
          onSaveSuccess={handleSaveSuccess}
        />
      )}
      <section className="pt-3">
        <div className="container">
          <div className="row">
            <SideNav />
            <div className="col-lg-8 col-xl-9">
              <div className="card shadow-sm border">
                <div className="card-header bg-transparent border-bottom">
                  <h4 className="card-header-title">
                    <i className="bi bi-input-cursor-text mx-2"></i><FormattedMessage id="custom.fields" />
                  </h4>
                </div>
                <div className="card-body border-bottom">
                  <div className="row g-4 justify-content-between align-items-center">
                    <div className="col-lg-9">
                      <h6>
                        <FormattedMessage id="custom.fields.description" />
                      </h6>
                    </div>
                    <div className="col-lg-3 col-xxl-3">
                      <Dropdown menu={menuProps} placement="bottomRight" trigger={['click']}>
                        <BlueButton
                          block
                          size="large"
                          type="primary"
                          className="btn btn-primary mb-0 float-end commonBlueBtn"
                          disabled={loading}
                        >
                          <i className="bi bi-plus-lg me-2"></i>
                          <FormattedMessage id="add.fields" />
                        </BlueButton>
                      </Dropdown>
                    </div>
                  </div>

                  <hr style={{ width: '-webkit-fill-available' }} />

                  <div className="row mt-4">
                    <Table
                      loading={loading}
                      pagination={false}
                      dataSource={customFields}
                      columns={columns}
                      rowKey="index"
                      components={{
                        body: {
                          wrapper: DraggableContainer,
                          row: DraggableBodyRow,
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CompanyCustomFields;
